import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Typography,
  TextField,
  Button,
  Alert,
  Card,
  CardContent,
  Container,
  CircularProgress
} from '@mui/material';
import { LockReset as LockResetIcon, CheckCircleOutline as CheckIcon } from '@mui/icons-material';

const ClientResetPassword = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const navigate = useNavigate();

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);

  const [companySettings, setCompanySettings] = useState({
    company_name: 'YinoWISP',
    company_logo: ''
  });

  useEffect(() => {
    // Si no hay token, mostrar error
    if (!token) {
      setError('El enlace de recuperación es inválido o ha expirado. Por favor, solicita uno nuevo.');
    }

    // Obtener configuraciones de empresa públicas (nombre y logo)
    const fetchCompanySettings = async () => {
      try {
        const response = await fetch('/api/public/company-settings');
        if (response.ok) {
          const data = await response.json();
          setCompanySettings({
            company_name: data.company_name || 'YinoWISP',
            company_logo: data.company_logo || ''
          });
        }
      } catch (error) {
        console.error('Error fetching company settings for reset password:', error);
      }
    };
    fetchCompanySettings();
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!token) {
      setError('Falta el token de recuperación. Solicita un nuevo enlace.');
      return;
    }

    if (!password || !confirmPassword) {
      setError('Por favor complete todos los campos');
      return;
    }

    if (password.length < 6) {
      setError('La contraseña debe tener al menos 6 caracteres');
      return;
    }

    if (password !== confirmPassword) {
      setError('Las contraseñas no coinciden');
      return;
    }

    try {
      setLoading(true);
      console.log('ClientResetPassword - Enviando solicitud de cambio de contraseña');

      const response = await fetch('/api/client/auth/reset-password', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ token, newPassword: password }),
      });

      const data = await response.json();

      if (response.ok) {
        setSuccess(true);
      } else {
        setError(data.message || 'Error al restablecer la contraseña. El enlace puede haber expirado.');
      }
    } catch (error) {
      console.error('ClientResetPassword - Error:', error);
      setError('Error en el servidor. Por favor intente más tarde.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        width: '100vw',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'radial-gradient(circle at center, #111827 0%, #030712 100%)',
        py: 4,
        px: 2,
        position: 'relative',
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          width: '350px',
          height: '350px',
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(99, 102, 241, 0.12) 0%, rgba(99, 102, 241, 0) 70%)',
          top: '10%',
          left: '15%',
          filter: 'blur(60px)',
          zIndex: 1
        },
        '&::after': {
          content: '""',
          position: 'absolute',
          width: '350px',
          height: '350px',
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(168, 85, 247, 0.12) 0%, rgba(168, 85, 247, 0) 70%)',
          bottom: '10%',
          right: '15%',
          filter: 'blur(60px)',
          zIndex: 1
        }
      }}
    >
      <Container maxWidth="xs" sx={{ position: 'relative', zIndex: 2 }}>
        <Card
          elevation={0}
          sx={{
            background: 'rgba(17, 24, 39, 0.65)',
            backdropFilter: 'blur(24px)',
            border: '1px solid rgba(255, 255, 255, 0.08)',
            borderRadius: '24px',
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.7)',
            overflow: 'hidden',
            transition: 'border 0.3s ease',
            '&:hover': {
              border: '1px solid rgba(255, 255, 255, 0.12)',
            }
          }}
        >
          <CardContent sx={{ p: { xs: 4, sm: 5 } }}>
            <Box sx={{ textAlign: 'center', mb: 4 }}>
              {companySettings.company_logo ? (
                <Box
                  component="img"
                  src={companySettings.company_logo}
                  alt={companySettings.company_name}
                  sx={{
                    width: 100,
                    height: 100,
                    objectFit: 'contain',
                    mb: 3,
                    mx: 'auto',
                    display: 'block',
                    borderRadius: '20px',
                    filter: 'drop-shadow(0 0 15px rgba(99, 102, 241, 0.4))',
                    animation: 'floatAndGlow 4s ease-in-out infinite'
                  }}
                />
              ) : (
                <LockResetIcon sx={{ fontSize: 60, color: '#38bdf8', mb: 2 }} />
              )}
              
              <Typography 
                variant="h4" 
                component="h1" 
                gutterBottom
                sx={{ 
                  fontWeight: 900, 
                  fontFamily: '"Outfit", "Inter", sans-serif', 
                  letterSpacing: '0.5px',
                  background: 'linear-gradient(90deg, #38bdf8 0%, #c084fc 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  mb: 1
                }}
              >
                Restablecer Contraseña
              </Typography>
              
              <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.5)', fontWeight: 500 }}>
                Crea una nueva contraseña segura para tu cuenta
              </Typography>
            </Box>

            {error && (
              <Alert 
                severity="error" 
                sx={{ 
                  mb: 3, 
                  borderRadius: '12px',
                  bgcolor: 'rgba(239, 68, 68, 0.1)',
                  color: '#f87171',
                  border: '1px solid rgba(239, 68, 68, 0.2)',
                  '& .MuiAlert-icon': { color: '#f87171' }
                }}
              >
                {error}
              </Alert>
            )}

            {success ? (
              <Box sx={{ textAlign: 'center', py: 2 }}>
                <CheckIcon sx={{ fontSize: 70, color: '#4ade80', mb: 2 }} />
                <Typography variant="h6" sx={{ color: '#ffffff', fontWeight: 700, mb: 1 }}>
                  ¡Contraseña Actualizada!
                </Typography>
                <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.6)', mb: 4 }}>
                  Tu contraseña ha sido restablecida exitosamente. Ya puedes iniciar sesión con tu nueva contraseña.
                </Typography>
                <Button
                  fullWidth
                  variant="contained"
                  onClick={() => navigate('/client/login')}
                  sx={{
                    py: 1.5,
                    borderRadius: '12px',
                    background: 'linear-gradient(95deg, #38bdf8 0%, #c084fc 100%)',
                    fontWeight: 700,
                    textTransform: 'none',
                    '&:hover': {
                      background: 'linear-gradient(95deg, #0ea5e9 0%, #a855f7 100%)',
                    }
                  }}
                >
                  Ir al Inicio de Sesión
                </Button>
              </Box>
            ) : (
              <Box component="form" onSubmit={handleSubmit} noValidate>
                <TextField
                  fullWidth
                  label="Nueva Contraseña"
                  name="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  margin="normal"
                  required
                  disabled={loading || !token}
                  autoFocus
                  InputLabelProps={{
                    sx: { color: 'rgba(255, 255, 255, 0.4)', '&.Mui-focused': { color: '#38bdf8' } }
                  }}
                  InputProps={{
                    sx: {
                      color: '#ffffff',
                      bgcolor: 'rgba(15, 23, 42, 0.45)',
                      borderRadius: '12px',
                      transition: 'all 0.3s ease',
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'rgba(255, 255, 255, 0.08)',
                      },
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'rgba(255, 255, 255, 0.2)',
                      },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#38bdf8',
                        boxShadow: '0 0 12px rgba(56, 189, 248, 0.15)'
                      }
                    }
                  }}
                />
                <TextField
                  fullWidth
                  label="Confirmar Nueva Contraseña"
                  name="confirmPassword"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  margin="normal"
                  required
                  disabled={loading || !token}
                  InputLabelProps={{
                    sx: { color: 'rgba(255, 255, 255, 0.4)', '&.Mui-focused': { color: '#c084fc' } }
                  }}
                  InputProps={{
                    sx: {
                      color: '#ffffff',
                      bgcolor: 'rgba(15, 23, 42, 0.45)',
                      borderRadius: '12px',
                      transition: 'all 0.3s ease',
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'rgba(255, 255, 255, 0.08)',
                      },
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'rgba(255, 255, 255, 0.2)',
                      },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#c084fc',
                        boxShadow: '0 0 12px rgba(192, 132, 252, 0.15)'
                      }
                    }
                  }}
                />
                
                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  disabled={loading || !token}
                  sx={{
                    mt: 4,
                    py: 1.5,
                    borderRadius: '12px',
                    background: 'linear-gradient(95deg, #38bdf8 0%, #c084fc 100%)',
                    fontWeight: 700,
                    fontSize: '0.95rem',
                    textTransform: 'none',
                    boxShadow: '0 4px 20px rgba(192, 132, 252, 0.3)',
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    '&:hover': {
                      background: 'linear-gradient(95deg, #0ea5e9 0%, #a855f7 100%)',
                      transform: 'translateY(-2px)',
                      boxShadow: '0 6px 24px rgba(192, 132, 252, 0.45)',
                    },
                    '&:active': {
                      transform: 'translateY(1px)',
                    },
                    '&.Mui-disabled': {
                      background: 'rgba(255, 255, 255, 0.12)',
                      color: 'rgba(255, 255, 255, 0.3)'
                    }
                  }}
                >
                  {loading ? <CircularProgress size={24} sx={{ color: '#ffffff' }} /> : 'Restablecer Contraseña'}
                </Button>

                {!token && (
                  <Button
                    fullWidth
                    variant="text"
                    onClick={() => navigate('/client/login')}
                    sx={{
                      mt: 2,
                      color: '#38bdf8',
                      fontWeight: 600,
                      textTransform: 'none',
                      '&:hover': { color: '#c084fc' }
                    }}
                  >
                    Volver al Inicio de Sesión
                  </Button>
                )}
              </Box>
            )}
          </CardContent>
        </Card>
      </Container>
    </Box>
  );
};

export default ClientResetPassword;
